import { supabase } from '@/integrations/supabase/client';
import {
  EmailTemplate,
  EmailTemplateFactory,
  EmailTemplatePayload,
  EmailTemplateRow,
} from '@/lib/admin/email-templates/models/email-template.model';

/** Codice Postgres di violazione di vincolo unico (qui: PK `template_key`). */
export const UNIQUE_VIOLATION_CODE = '23505';

/** True se l'errore Supabase è una violazione di chiave duplicata. */
export const isUniqueViolation = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  (error as { code?: string }).code === UNIQUE_VIOLATION_CODE;

/**
 * Lista completa dei template email/notifiche.
 *
 * La tabella è piccola e la pagina admin non pagina: si scarica tutto una volta e
 * si filtra/ordina client-side. La RLS (`FOR ALL` per admin approvato) resta
 * l'unica fonte di verità sui permessi: qui non si fa nessun controllo di ruolo.
 */
export const fetchEmailTemplates = async (): Promise<EmailTemplate[]> => {
  const { data, error } = await supabase
    .from('email_templates')
    .select('*')
    .order('template_key', { ascending: true });

  if (error) {
    console.error('Error fetching email templates:', error);
    throw error;
  }

  return ((data ?? []) as EmailTemplateRow[]).map(EmailTemplateFactory.fromRow);
};

/**
 * Insert di un nuovo template. `created_at`/`updated_at` non vengono inviati:
 * li gestiscono default e trigger a DB.
 */
export const createEmailTemplate = async (
  payload: EmailTemplatePayload
): Promise<EmailTemplate> => {
  const { data, error } = await supabase
    .from('email_templates')
    .insert(payload)
    .select('*')
    .single();

  if (error || !data) {
    console.error('Error creating email template:', error);
    throw error ?? new Error('createEmailTemplate returned no data');
  }

  return EmailTemplateFactory.fromRow(data as EmailTemplateRow);
};

/**
 * Update di un template esistente, identificato dalla PK `template_key`.
 * `key` è la chiave ORIGINALE: la chiave non è modificabile in edit, quindi il
 * payload non la cambia mai.
 */
export const updateEmailTemplate = async (
  key: string,
  payload: EmailTemplatePayload
): Promise<EmailTemplate> => {
  const { template_key, ...updatable } = payload;
  void template_key;

  const { data, error } = await supabase
    .from('email_templates')
    .update(updatable)
    .eq('template_key', key)
    .select('*')
    .single();

  if (error || !data) {
    console.error('Error updating email template:', error);
    throw error ?? new Error('updateEmailTemplate returned no data');
  }

  return EmailTemplateFactory.fromRow(data as EmailTemplateRow);
};

export const deleteEmailTemplate = async (key: string): Promise<void> => {
  const { error } = await supabase
    .from('email_templates')
    .delete()
    .eq('template_key', key);

  if (error) {
    console.error('Error deleting email template:', error);
    throw error;
  }
};
